import { AlertTriangle, Clock, Download, Upload } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { uploadImage } from "../../lib/api";

// ============================================================================
// Shared helpers used across multiple admin tabs.
// Extracted from Admin.jsx during the refactor so individual tab files (and any
// future ones — ProductionTab, AnalyticsTab, etc.) can import them without
// pulling the entire 2,600-line Admin shell.
// ============================================================================

// ===== REPLY-QUICKLY URGENCY =====
// Quotes still "new" > URGENT_HOURS get flagged red.
// Orders still "pending" fulfillment > URGENT_HOURS get flagged red.
export const URGENT_HOURS = 24;
export const STALE_HOURS = 72;

export function hoursSince(iso) {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  if (isNaN(t)) return 0;
  return Math.max(0, (Date.now() - t) / 3_600_000);
}

export function ageLabel(hours) {
  if (hours < 1) return `${Math.round(hours * 60)}m ago`;
  if (hours < 24) return `${Math.round(hours)}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export function urgencyLevel(itemCreatedAt, isOpen) {
  if (!isOpen) return null;
  const h = hoursSince(itemCreatedAt);
  if (h >= STALE_HOURS) return "stale";
  if (h >= URGENT_HOURS) return "urgent";
  return null;
}

export function UrgencyBadge({ createdAt, isOpen }) {
  const level = urgencyLevel(createdAt, isOpen);
  if (!level) return null;
  const cls = level === "stale"
    ? "bg-[var(--tc-red)] text-white border-[var(--tc-red)] animate-pulse"
    : "bg-[var(--tc-red)]/15 text-[var(--tc-red)] border-[var(--tc-red)]/50";
  const label = level === "stale" ? "STALE — REPLY NOW" : "URGENT — REPLY";
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 border text-[9px] font-mono tracking-widest uppercase ${cls}`}
      data-testid={`urgency-${level}`}
    >
      <AlertTriangle size={10} /> {label}
    </span>
  );
}

export function AgeChip({ createdAt }) {
  return (
    <div className="font-mono text-[10px] text-zinc-500 mt-1 flex items-center gap-1">
      <Clock size={10} />{ageLabel(hoursSince(createdAt))}
    </div>
  );
}

// ===== CSV EXPORT =====
export function csvEscape(v) {
  if (v === null || v === undefined) return "";
  const s = String(v).replace(/\r?\n/g, " ").replace(/"/g, '""');
  return /[",]/.test(s) ? `"${s}"` : s;
}

export function downloadCsv(filename, headers, rows) {
  const csv = [
    headers.join(","),
    ...rows.map((r) => headers.map((h) => csvEscape(r[h])).join(",")),
  ].join("\r\n");
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function ExportCsvButton({ onClick, count, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      disabled={count === 0}
      className="inline-flex items-center gap-2 px-4 py-2 border border-[#222] hover:border-[var(--tc-red)] hover:text-[var(--tc-red)] font-mono text-[11px] tracking-widest uppercase disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
    >
      <Download size={12} /> Export CSV · {count}
    </button>
  );
}

// ===== APPENDED FROM Admin.jsx REFACTOR =====

export function Info({ label, value }) {
  return (
    <div>
      <div className="font-mono text-[10px] tracking-widest text-zinc-500">{label.toUpperCase()}</div>
      <div className="text-sm mt-1">{value}</div>
    </div>
  );
}

export function ImageField({ label, value, onChange, fullWidth }) {
  const [uploading, setUploading] = useState(false);
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Max 8MB"); return; }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const res = await uploadImage(ev.target.result, file.name);
        onChange(res.url);
        toast.success("Uploaded");
      } catch {
        toast.error("Upload failed");
      } finally {
        setUploading(false);
      }
    };
    reader.readAsDataURL(file);
  };
  return (
    <div className={fullWidth ? "sm:col-span-2" : ""}>
      <label className="font-mono text-[10px] tracking-widest text-zinc-500">{label.toUpperCase()} URL</label>
      <div className="flex gap-2 mt-1">
        <input
          data-testid={`field-image-${label.toLowerCase().replace(/\s/g, "-")}`}
          type="text" value={value} onChange={(e) => onChange(e.target.value)}
          placeholder="https://..."
          className="flex-1 bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-2 font-body text-sm outline-none"
        />
        <label className="cursor-pointer flex items-center gap-1 px-3 py-2 border border-[#222] hover:border-[var(--tc-red)] font-mono text-[10px] tracking-widest whitespace-nowrap">
          <Upload size={12} /> {uploading ? "..." : "UPLOAD"}
          <input type="file" accept="image/*" className="hidden" onChange={handleFile} />
        </label>
      </div>
      {value && <div className="mt-2 w-20 h-20 border border-[#222] overflow-hidden"><img src={value} alt="" className="w-full h-full object-cover" /></div>}
    </div>
  );
}

export function AField({ label, type = "text", value, onChange, fullWidth }) {
  return (
    <div className={fullWidth ? "sm:col-span-2" : ""}>
      <label className="font-mono text-[10px] tracking-widest text-zinc-500">{label.toUpperCase()}</label>
      <input
        type={type} value={value} onChange={onChange}
        className="w-full bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-2 mt-1 font-body text-sm outline-none"
      />
    </div>
  );
}


// ===== ABANDONED CARTS TAB =====
// Lists carts where the customer hit checkout but didn't pay within the
// threshold window. Admin can fire a one-shot recovery email per row.
// Auto-cleared when payment lands (via /api/checkout/status).
